import { downloadFile, hasValue, log, SortTypeEnum } from "@repo/helpers";
import { KeyedArray } from "@repo/models";
import { format } from "date-fns";
import { MouseEvent as ReactMouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import GridMarkup from "./markup";
import style from "./style.module.scss";
import { ColumnConfiguration, GridBaseProps, GridSaveState, SortBy } from "./types";

export function Grid<T>(props: GridBaseProps<T>) {
	const { resizeable = false } = props;
	const isInitialMount = useRef(true);
	const currentResizeTh = useRef<HTMLTableCellElement>(null);
	const offset = useRef(0);
	const tableWidth = useRef(0);
	const tableRef = useRef<HTMLTableElement>(null);
	const columnWidth = useRef(0);
	const activeColumnId = useRef<string | null>(null);
	const minWidth = useRef(0);

	const [columns, setColumns] = useState([...props.columns]);
	const [currentlyResizing, setCurrentlyResizing] = useState(false);
	const [pageSize, setPageSize] = useState(props.pageSize);
	const [exporting, setExporting] = useState(false);

	const [columnOrder, setColumnOrder] = useState<string[]>(columns.map((c) => c.id));

	const columnMap = useMemo(() => {
		return new KeyedArray(columns, (c) => c.id);
	}, [columns]);
	const sortedColumns = useMemo(() => {
		return columnOrder.map((colId) => columnMap.get(colId)).filter(hasValue);
	}, [columnOrder, columns]);

	useEffect(() => {
		if (props.stateful) {
			loadGridConfiguration();
		}
		setColumns([...columns]);

		isInitialMount.current = false;
		return removeEvents;
	}, []);

	useEffect(() => {
		if (!resizeable) {
			removeEvents();
			return;
		}
		document.addEventListener("mousemove", resizeColumn);
		document.addEventListener("mouseup", clearCurrentResizeTh);
		if (tableRef.current) {
			// initialize table width
			tableWidth.current = tableRef.current.offsetWidth;
			const pageColumns = tableRef.current.querySelectorAll<HTMLTableHeaderCellElement>(
				`th.${style.resizeableHeader}`,
			);
			// set the default width for each column to prevent table from resizing columns on resize
			for (let i = 0; i < pageColumns.length; i++) {
				const currentWidth = pageColumns[i].offsetWidth;
				const width = Math.max(isNaN(currentWidth) ? 0 : currentWidth, sortedColumns[i].minWidth);
				sortedColumns[i].width = width + "px";
				pageColumns[i].style.width = width + "px";
			}
			setColumns([...sortedColumns]);
		}
	}, [resizeable]);

	useEffect(() => {
		if (isInitialMount.current) return;
		if (props.setItemsPerPage) props.setItemsPerPage(pageSize);
		else log(`${props.id} Grid has not been set up for custom items per page.`);
	}, [pageSize]);

	useEffect(() => {
		setColumns((prev) => {
			if (!prev?.length) return [...props.columns];

			try {
				const prevByKey = new Map(prev.map((c) => [c.id, c]));
				const cols = [...props.columns];

				return cols.map((c) => {
					const key = c.id;
					const prevCol = prevByKey.get(key);
					if (prevCol) {
						c.sortBy = prevCol.sortBy;
						c.width = prevCol.width;
						c.visible = prevCol.visible;
					}
					return c;
				});
			} catch {
				return [...props.columns];
			}
		});
	}, [props.columns]);

	useEffect(() => {
		if (!props.loading) {
			const pageColumns = tableRef.current.querySelectorAll<HTMLTableCellElement>("." + style.resizeableHeader);

			for (let i = 0; i < pageColumns.length; i++) {
				const width = Math.max(pageColumns[i].offsetWidth || 0, columns[i].minWidth || 0);
				columns[i].width = width + "px";
				pageColumns[i].style.width = width + "px";
			}
		}
	}, [props.loading]);

	function removeEvents() {
		document.removeEventListener("mousemove", resizeColumn);
		document.removeEventListener("mouseup", clearCurrentResizeTh);
	}

	/**
	 * Use the function passed in props to call the api, get any grid saved state
	 * and send it to the gridConfigurationLoaded function
	 */
	function loadGridConfiguration() {
		if (props.loadGridState) {
			props.loadGridState(gridConfigurationLoaded);
		}
	}
	/**
	 * Callback when grid configuration is loaded from the api
	 * @param configuration
	 */
	function gridConfigurationLoaded(configuration: GridSaveState) {
		const stateColumns = columns.map((c) => c);
		const newColumnOrder = [];
		// if the configuration is blank (no saved state)
		if (!hasValue(configuration?.columns)) return;
		let sortBySet = null;
		// restore the configuration for each column
		configuration.columns.forEach((column) => {
			// if the column was selected for sorting, store it so the grid can be refreshed with the correct
			// sorting chosen
			if ((column.sortBy ?? SortTypeEnum.none) !== SortTypeEnum.none) {
				sortBySet = {
					order: column.sortBy,
					name: column.propertyName || column.name,
				} as SortBy;
			}
			// Look for the column by name and update it's configuration
			const matchingColumns = stateColumns.filter((c) => c.name === column.name);
			for (const matchingColumn of matchingColumns) {
				matchingColumn.visible = column.visible ?? true;
				matchingColumn.width = column.width ?? "";
				matchingColumn.sortBy = column.sortBy ?? SortTypeEnum.none;
				newColumnOrder.push(matchingColumn.id);
			}
		});

		// If some columns were not found in the saved state, add them to the end
		stateColumns.forEach((c) => {
			if (!newColumnOrder.includes(c.id)) {
				newColumnOrder.push(c.id);
			}
		});

		setColumns(stateColumns);
		if (newColumnOrder.length > 0) {
			setColumnOrder(newColumnOrder);
		}
		// have to adjust the table width since we adjusted the column widths
		if (hasValue(tableRef.current)) {
			tableWidth.current = columns
				.map((column) => parseFloat(column.width.replace(/[^0-9.]/, "")))
				.reduce((total, width) => total + width);
			tableRef.current.style.width = tableWidth.current + "px";
		}
		// if the grid was saved while it was sorted, refresh the grid with the selected sort option
		if (hasValue(sortBySet)) {
			props.getData(1, { "sortBy.name": sortBySet.name, "sortBy.order": sortBySet.order } as any);
		}
	}

	/**
	 * Serialize the grid configuration and save it to the API through the saveGridState property
	 */
	function saveGridConfiguration() {
		if (tableRef.current && props.stateful && props.saveGridState) {
			props.saveGridState(
				JSON.stringify({
					columns: generateColumnConfiguration(),
					pageSize: props.pageSize,
				}),
			);
		}
	}

	/**
	 * Pass an empty string for the grid configuration to the saveGridState prop in order to clear the saved config
	 */
	function clearGridConfiguration() {
		if (props.stateful && props.saveGridState) {
			props.saveGridState("");

			const newColumns = [...props.columns];
			newColumns.forEach((column) => {
				column.visible = true;
				column.width = column.minWidth + "px";
			});
			setColumns(newColumns);
		}
	}

	async function buildExportData() {
		const { getRecordsToExport } = props;
		const rows = await getRecordsToExport();
		if (!rows.length) {
			alert("No data to export");
			return null;
		}
		const displayValues = [] as any[];
		const visibleColumns = sortedColumns.filter((c) => c.visible && c.exportable !== false);
		const columnsToKeep = new KeyedArray(visibleColumns, (k) => k.name);

		for (const rowItem of rows) {
			const values = [] as any[];
			const columnsInThisRow = columnsToKeep.data.map((c) => c);

			for (const c of columnsInThisRow) {
				const { name } = c;
				let value: any = "";
				try {
					value = c.getValue(rowItem);
				} catch (e) {
					console.error(`Error getting value for column ${name}`, e);
				}

				if (!hasValue(value)) {
					values.push("");
					continue;
				}

				if (typeof value === "object") {
					// Check if it's a date or some other serializable object
					// Date objects have no keys but are serializable
					if (!(value instanceof Date) && Object.keys(value).length > 0) {
						// Likely a React element or complex object, remove column from future rows
						columnsToKeep.removeId(name);
						continue;
					}
				}

				values.push(value);
			}
			displayValues.push(values);
		}
		return { rows: displayValues, headers: columnsToKeep.map((c) => c.name) };
	}

	async function excelExport() {
		const { exportable, getRecordsToExport, gridNameForExport, apiRoute } = props;
		if (!exportable || !getRecordsToExport) return;
		try {
			const data = await buildExportData();
			if (!data) {
				setExporting(false);
				return;
			}
			const xhr = new XMLHttpRequest();
			xhr.responseType = "blob";
			xhr.onload = () => {
				generateFileDownload(
					xhr.status,
					xhr.response,
					xhr.getResponseHeader("content-disposition"),
					gridNameForExport,
					"export",
					"xlsx",
					"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet ",
					"excel",
				);
				setExporting(false);
			};
			xhr.onerror = () => {
				console.error("XHR error for GenerateExcelFile");
				alert("Unable to generate excel file");
				setExporting(false);
			};
			xhr.open("POST", `${apiRoute}/GenerateExcelFile`);
			const formData = new FormData();
			formData.append("rows", JSON.stringify(data.rows));
			formData.append("headers", JSON.stringify(data.headers));
			xhr.send(formData);
		} catch (e) {
			console.error("Export error:", e);
			log(e, "error");
			alert("Unable to create export");
			setExporting(false);
		}
	}

	async function csvExport() {
		const { exportable, getRecordsToExport, gridNameForExport, apiRoute } = props;
		if (!exportable || !getRecordsToExport) return;
		try {
			const data = await buildExportData();
			if (!data) {
				setExporting(false);
				return;
			}
			const xhr = new XMLHttpRequest();
			xhr.responseType = "blob";
			xhr.onload = () => {
				generateFileDownload(
					xhr.status,
					xhr.response,
					xhr.getResponseHeader("content-disposition"),
					gridNameForExport,
					"export",
					"csv",
					"text/csv",
					"csv",
				);
				setExporting(false);
			};
			xhr.onerror = () => {
				console.error("XHR error for GenerateCsvFile");
				alert("Unable to generate csv file");
				setExporting(false);
			};
			xhr.open("POST", `${apiRoute}/GenerateCsvFile`);
			const formData = new FormData();
			formData.append("rows", JSON.stringify(data.rows));
			formData.append("headers", JSON.stringify(data.headers));
			xhr.send(formData);
		} catch (e) {
			console.error("Export error:", e);
			log(e, "error");
			alert("Unable to create export");
			setExporting(false);
		}
	}

	async function excelExportClicked() {
		setExporting(true);
		await excelExport();
	}

	async function csvExportClicked() {
		setExporting(true);
		await csvExport();
	}

	function generateFileDownload(
		status: number,
		response: any,
		disposition: string | null,
		gridNameForExport: string | undefined,
		defaultFileName: string,
		extension: string,
		mimeType: string,
		typeLabel: string,
	) {
		if (status === 200) {
			if (response == null) {
				alert(`Unable to generate ${typeLabel} file`);
				return;
			}
			const blob = new Blob([response], {
				type: mimeType,
			});

			const url = window.URL.createObjectURL(blob);

			const fileName = gridNameForExport ?? defaultFileName;

			downloadFile(url, `${fileName} - ${format(new Date(), "yyyyMMddhhmmss")}.${extension}`);
			window.URL.revokeObjectURL(url);
		} else {
			alert(`Unable to generate ${typeLabel} file`);
		}
	}

	function generateColumnConfiguration() {
		return sortedColumns.map((column) => {
			return {
				name: column.name,
				propertyName: column.propertyName,
				visible: column.visible,
				width: column.width,
				sortBy: column.sortBy,
				clientTemplate: column.clientTemplate,
			} as ColumnConfiguration;
		});
	}

	/**
	 * Set the sort value for the selected column
	 * @param columnIndex Index of the column
	 */
	const sortOnColumn = useCallback(
		(id: string) => {
			// if not sortable or if being resized, ignore the click event
			if (!props.sortable || currentResizeTh.current) return;
			// currentlyResizing is set on mousedown of a grid table head element
			// on mouse up is also seen as clicking on a table head element
			// Since mouseup is triggered before onclick, letting go of the
			// resize event is seen as clicking on the column as well.
			// Therefore, if the mouse up event clears the currentResizeTh
			// before the sort on column function, this flag will catch
			// that and prevent the sort on column
			if (currentlyResizing) {
				setCurrentlyResizing(false);
				return;
			}

			const column = columnMap.get(id);
			const originalSort = column.sortBy;

			//So we don't do an unnecessary data load
			if (!column.sortable) return;

			columns.forEach((c) => (c.sortBy = SortTypeEnum.none));
			if (originalSort === SortTypeEnum.none) {
				column.sortBy = SortTypeEnum.ascending;
			} else if (originalSort === SortTypeEnum.ascending) {
				column.sortBy = SortTypeEnum.descending;
			}
			setColumns([...columns]);
			props.getData(1, {
				"sortBy.order": column.sortBy,
				"sortBy.name": column.propertyName || column.name,
			} as any);
		},
		[columns, props.getData, props.sortable],
	);

	/**
	 * Keep a reference of what column is currently being resized so that if the cursor goes over
	 * another column, it doesn't resize another column
	 * @param event
	 */
	const setCurrentResizeTh = useCallback(
		(event: ReactMouseEvent<HTMLDivElement>) => {
			currentResizeTh.current = event.currentTarget.parentElement as HTMLTableHeaderCellElement;
			columnWidth.current = currentResizeTh.current.offsetWidth;
			offset.current = currentResizeTh.current.offsetWidth - event.pageX;
			activeColumnId.current = event.currentTarget.getAttribute("data-id") as string;
			if (currentResizeTh.current.style.minWidth === "") {
				minWidth.current = 0;
			} else {
				minWidth.current = parseFloat(currentResizeTh.current.style.minWidth.replace(/[^0-9.]/, ""));
			}
			setCurrentlyResizing(true);
		},
		[columns],
	);

	/**
	 * Clear the reference of the column being resized
	 */
	function clearCurrentResizeTh() {
		if (hasValue(currentResizeTh.current)) {
			currentResizeTh.current = null;
			columnMap.get(activeColumnId.current).width = columnWidth.current + "px";
			setColumns(columns);
		}
	}

	/**
	 * Mouse move over the table event. Will only resize when a TH resize element has been clicked and dragged
	 * @param event
	 */
	function resizeColumn(event: MouseEvent) {
		// a th element is being resized
		if (currentResizeTh.current) {
			// get the size change for the column. If the adjust value is less
			// than 75 px, keep the column to 75 px to prevent the column from
			// being collapsed
			const adjust = Math.max(10, minWidth.current, offset.current + event.pageX);
			// if the table ref if active (it should always be but just in case)
			// then adjust the table size to accommodate the change in column size
			if (hasValue(tableRef.current)) {
				tableWidth.current = tableWidth.current - columnWidth.current + adjust;
				tableRef.current.style.width = tableWidth.current + "px";
			}
			// set the new column size
			columnWidth.current = adjust;
			currentResizeTh.current.style.width = columnWidth.current + "px";
		}
	}

	function updateColumnVisibility(id: string, visibility: boolean) {
		const column = columns.find((c) => c.id === id);
		if (column) {
			column.visible = visibility;
			setColumns([...columns]);
		}
		setColumns([...columns]);
	}

	function refresh() {
		if (props.refresh) props.refresh();
		else props.getData(props.currentPage);
	}

	return (
		<GridMarkup<T>
			{...props}
			isEnriching={props.isEnriching}
			pageSize={pageSize}
			columns={sortedColumns}
			columnOrder={columnOrder}
			setColumnOrder={setColumnOrder}
			sortEventHandler={sortOnColumn}
			refresh={refresh}
			updateColumnVisibility={updateColumnVisibility}
			tableRef={tableRef}
			excelExportEvent={excelExportClicked}
			setItemsPerPage={setPageSize}
			saveGridConfiguration={saveGridConfiguration}
			clearGridConfiguration={clearGridConfiguration}
			setCurrentResizeTh={setCurrentResizeTh}
			exportDisabled={exporting}
			csvExportEvent={csvExportClicked}
			csvExportDisabled={exporting}
		/>
	);
}

export default Grid;
export * from "./types";
export * from "./models";
